import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  Theme,
} from '@material-ui/core';
import { Check, Close } from '@material-ui/icons';
import {
  Color,
  Column,
  Columns,
  Image,
  Inline,
  Stack,
} from '@superdispatch/ui';
import { Button, TextBox } from '@superdispatch/ui-lab';
import { FunctionComponent, ReactNode, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { trackLoadboardEvent } from '../../data/LoadboardAnalytics';
import { useBecomeVerifiedCarrierNavigation } from './LoadboardAccessAPI';
import LoadboardTrailDarkIcon from './LoadboardAccessDarkIcon.svg';
import { useLoadboardAccessState } from './LoadboardAccessDialogController';
import LoadboardTrailIcon from './LoadboardAccessIcon.svg';

const StyledDialog: FunctionComponent<DialogProps> = styled(Dialog)(
  ({ theme }: { theme: Theme }) => css`
    z-index: ${
      theme.zIndex.modal + 1
    } !important; /*should be on top of other dialogs*/

    & .MuiDialogActions-root {
      padding: 16px;
      justify-content: center;
    }

    & .MuiPaper-root {
      padding: 16px;
    }

    ${theme.breakpoints.up('sm')} {
      & .MuiPaper-root {
        min-height: 520px;
        min-width: 720px;
      }

      & .MuiDialogContent-root {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  `,
);

interface CheckItemProp {
  children: ReactNode;
}

function CheckItem({ children }: CheckItemProp) {
  return (
    <Inline noWrap={true}>
      <Check htmlColor={Color.Green300} />

      {children}
    </Inline>
  );
}

interface LoadboardAccessDialogProps {
  open: boolean;
  onClose: () => void;
}

export function LoadboardAccessDialog({
  open,
  onClose,
}: LoadboardAccessDialogProps) {
  return (
    <StyledDialog open={open} maxWidth="lg" onClose={onClose}>
      <LoadboardAccessDialogContent onClose={onClose} />
    </StyledDialog>
  );
}

interface LoadboardAccessDialogContentProps {
  onClose: () => void;
}

function LoadboardAccessDialogContent({
  onClose,
}: LoadboardAccessDialogContentProps) {
  const becomeVerifiedCarrier = useBecomeVerifiedCarrierNavigation();
  const [isFirstView] = useLoadboardAccessState();

  useEffect(() => {
    trackLoadboardEvent({
      name: 'Carrier Viewed Become Verified Popup',
      utm_medium: isFirstView ? 'First View' : 'Verified Carrier Banner',
    });
  }, [isFirstView]);

  return (
    <>
      <DialogTitle>
        <Columns align="top">
          <Column>
            <TextBox align="center" variant="heading-1">
              Become a Verified Carrier
            </TextBox>
          </Column>

          <Column width="content">
            <IconButton size="small" aria-label="close" onClick={onClose}>
              <Close />
            </IconButton>
          </Column>
        </Columns>
      </DialogTitle>

      <DialogContent>
        <Inline horizontalAlign="center" space="xxlarge">
          <Image
            alt=""
            width="235"
            height="200"
            src={LoadboardTrailIcon}
            srcDark={LoadboardTrailDarkIcon}
          />
          <Stack space="small">
            <TextBox variant="heading-4">
              As a Verified Carrier get unlimited <br /> access to the load
              board and...
            </TextBox>

            <CheckItem>Book loads faster</CheckItem>
            <CheckItem>Save time with saved documents</CheckItem>
            <CheckItem>Get paid faster</CheckItem>
            <CheckItem>Establish trust with Shippers</CheckItem>
          </Stack>
        </Inline>
      </DialogContent>

      <DialogActions>
        <Inline horizontalAlign="center" space="small">
          <Button variant="neutral" onClick={onClose}>
            Do it Later
          </Button>

          <Button
            onClick={() => {
              onClose();
              becomeVerifiedCarrier({ source: 'become_verified_popup' });
            }}
          >
            Become Verified Carrier
          </Button>
        </Inline>
      </DialogActions>
    </>
  );
}
